import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useCredit } from "../context/CreditContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { fmtUsd } from "../data/casesData";
import { REEL_SOUNDS, getReelSound, setReelSound, playReelSample } from "../lib/sounds";
import SupportBot from "./SupportBot";
import { Banknote, Truck, History, Loader2, User, Volume2, Play, Check, Headset, MessageCircle } from "lucide-react";

const COLORS = { pending: "#D97706", approved: "#0891B2", fulfilled: "#16A34A", rejected: "#DB2777" };

export default function ProfileModal({ open, onOpenChange }) {
  const { user } = useAuth();
  const { listWithdrawals } = useCredit();
  const [items, setItems] = useState(null);
  const [sound, setSound] = useState(() => getReelSound());
  const [supportOpen, setSupportOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    let active = true;
    (async () => {
      try { const w = await listWithdrawals(); if (active) setItems(w); }
      catch (_) { if (active) setItems([]); }
    })();
    return () => { active = false; };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const chooseSound = (key) => { setSound(key); setReelSound(key); playReelSample(key); };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#19142A] dark:bg-[#141021] border border-[#2A2440] dark:border-[#2A2440] rounded-lg max-w-md max-h-[88vh] overflow-y-auto text-white dark:text-white">
        <DialogHeader><DialogTitle className="font-cases uppercase">Profile</DialogTitle></DialogHeader>
        {user && (
          <div data-testid="profile-account" className="flex items-center gap-3 border border-[#2A2440] dark:border-[#2A2440] p-3">
            <span className="h-10 w-10 rounded-full bg-[#7C4DFF] dark:bg-[#9945FF] text-white flex items-center justify-center"><User className="h-5 w-5" /></span>
            <div className="min-w-0">
              <div className="font-semibold truncate">{user.name}</div>
              <div className="text-xs text-[#8A90A0] truncate">{user.email}</div>
            </div>
          </div>
        )}

        {/* ── Settings: reel sound ── */}
        <div className="mt-2" data-testid="settings-section">
          <div className="text-xs font-bold uppercase text-[#8A90A0] flex items-center gap-1.5 mb-2"><Volume2 className="h-3.5 w-3.5" />Sound · Case-opening reel</div>
          <div className="space-y-1.5">
            {REEL_SOUNDS.map((s) => {
              const active = sound === s.key;
              return (
                <div key={s.key} data-testid={`sound-option-${s.key}`} onClick={() => chooseSound(s.key)}
                  className={`flex items-center gap-3 px-3 py-2 border cursor-pointer transition-colors ${active ? "border-[#8B5CF6] bg-[#8B5CF6]/10" : "border-[#2A2440] dark:border-[#2A2440] hover:border-[#3A2F5C] dark:hover:border-[#3A2F5C]"}`}>
                  <span className={`h-4 w-4 rounded-full border flex items-center justify-center shrink-0 ${active ? "border-[#8B5CF6] bg-[#8B5CF6] text-white" : "border-[#3A2F5C]"}`}>
                    {active && <Check className="h-3 w-3" strokeWidth={3} />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold leading-tight">{s.label}</div>
                    <div className="text-[11px] text-[#8A90A0] truncate">{s.desc}</div>
                  </div>
                  {s.key !== "off" && (
                    <button data-testid={`sound-preview-${s.key}`} onClick={(e) => { e.stopPropagation(); playReelSample(s.key); }}
                      aria-label={`Preview ${s.label}`}
                      className="h-7 w-7 shrink-0 rounded-full flex items-center justify-center border border-[#2A2440] dark:border-[#2A2440] text-white dark:text-white hover:bg-[#19142A] dark:hover:bg-[#2A2440] transition-colors">
                      <Play className="h-3.5 w-3.5" fill="currentColor" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          <div className="text-[10px] text-[#9AA0AE] mt-1.5">Battles keep the quiet classic blip so multiple reels don't clash.</div>
        </div>

        {/* ── Support ── */}
        <div className="mt-3" data-testid="support-section">
          <div className="text-xs font-bold uppercase text-[#8A90A0] flex items-center gap-1.5 mb-2"><Headset className="h-3.5 w-3.5" />Support</div>
          <button data-testid="open-support-btn" onClick={() => { onOpenChange(false); setSupportOpen(true); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 border border-[#2A2440] dark:border-[#2A2440] hover:border-[#8B5CF6] hover:bg-[#8B5CF6]/5 transition-colors text-left">
            <span className="h-9 w-9 rounded-full bg-gradient-to-br from-[#22D3EE] to-[#8B5CF6] text-white flex items-center justify-center shrink-0"><MessageCircle className="h-4 w-4" /></span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold">Live Support</div>
              <div className="text-[11px] text-[#8A90A0]">Chat with our assistant — get instant answers, 24/7.</div>
            </div>
          </button>
        </div>

        <div className="mt-3">
          <div className="text-xs font-bold uppercase text-[#8A90A0] flex items-center gap-1.5 mb-2"><History className="h-3.5 w-3.5" />Withdrawal history</div>
          {items === null ? (
            <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#8A90A0]" /></div>
          ) : items.length === 0 ? (
            <div data-testid="history-empty" className="py-8 text-center text-xs text-[#8A90A0] flex flex-col items-center gap-1">
              <History className="h-6 w-6" /> No withdrawals yet. Cash out or ship items and they'll show here.
            </div>
          ) : (
            <div data-testid="withdraw-history" className="max-h-80 overflow-y-auto space-y-2">
              {items.map((w) => (
                <div key={w.id} data-testid="history-row" className="border border-[#2A2440] dark:border-[#2A2440] px-3 py-2 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold flex items-center gap-1.5">
                      {w.type === "cash" ? <Banknote className="h-3.5 w-3.5 text-[#16A34A]" /> : <Truck className="h-3.5 w-3.5 text-[#8B5CF6]" />}
                      {w.type === "cash" ? "Cash out" : "Ship"} · {fmtUsd(w.amount ?? w.item_value)}
                    </div>
                    <div className="text-[10px] text-[#8A90A0] truncate">
                      {w.type === "cash" ? `${w.item_name ? (w.item_count > 1 ? w.item_count + " items" : w.item_name) + " · " : ""}${(w.method || "").toUpperCase()} · ${w.destination}` : (w.item_count > 1 ? `${w.item_count} items` : w.item_name)}
                      {" · "}{w.created_at ? new Date(w.created_at).toLocaleDateString() : ""}
                    </div>
                  </div>
                  <span className="px-2 py-0.5 text-[10px] font-bold uppercase shrink-0"
                    style={{ color: COLORS[w.status] || "#8A90A0", border: `1px solid ${(COLORS[w.status] || "#8A90A0")}55` }}>
                    {w.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
    <SupportBot open={supportOpen} onClose={() => setSupportOpen(false)} />
    </>
  );
}
